import type { SailPlotMarkColorMode } from "@heiselanalytics/sailplot";
import { createPlatformPageExtensions } from "../extensions/platformPages";
import { coreAssets } from "../tenants/coreAssets";
import {
  sailPlotCustomizedPageTitle,
  sailPlotFaviconDataUrl,
} from "./sailplotFavicon";
import type { PlatformTenant } from "./types";

interface CustomerLink {
  id: "website" | "privacy" | "imprint";
  label: string;
  url: string;
}

interface CustomerLogoAction {
  id: string;
  type: "website" | "whatsapp" | "email" | "phone" | "link";
  label: string;
  value: string;
}

interface LogoElementBase {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LogoTextElement extends LogoElementBase {
  type: "text";
  text: string;
  color: string;
  fontFamily?:
    "arial" | "verdana" | "trebuchet" | "georgia" | "times" | "courier";
  fontSize: number;
  fontWeight: 400 | 600 | 700 | 800;
  align: "left" | "center" | "right";
}

interface LogoImageElement extends LogoElementBase {
  type: "image";
  name: string;
  src: string;
}

interface LogoDesign {
  backgroundColor: string;
  cornerRadius: number;
  elements: Array<LogoTextElement | LogoImageElement>;
}

export interface CustomerTenantRecord {
  id: string;
  domain: string;
  identityName: string;
  primaryColor: string;
  defaultTheme: "light" | "dark";
  languageMode: "de" | "en" | "both";
  markColorMode: SailPlotMarkColorMode;
  markCustomColor: string;
  startLineFlagColor: string;
  logoDataUrl: string;
  links: CustomerLink[];
  logoLinks?: Array<{ label: string; url: string }>;
}

interface RuntimeTenantEnvelope {
  id: string;
  domain: string;
  configuration: {
    schemaVersion: 1;
    identityName: string;
    primaryColor: string;
    defaultTheme: "light" | "dark";
    languageMode: "de" | "en" | "both";
    preferredSubdomain: string;
    markColorMode: SailPlotMarkColorMode;
    markCustomColor: string;
    startLineFlagColor: string;
    links: CustomerLink[];
    logoActions: CustomerLogoAction[];
    logo: LogoDesign;
  };
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isHexColor = (value: unknown): value is string =>
  typeof value === "string" && /^#[0-9a-f]{6}$/iu.test(value);

const escapeXml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");

const boundedNumber = (value: unknown, min: number, max: number) =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max;

const logoFontFamily = (family: LogoTextElement["fontFamily"]) =>
  ({
    arial: "Arial, Helvetica, sans-serif",
    verdana: "Verdana, Geneva, sans-serif",
    trebuchet: "Trebuchet MS, Arial, sans-serif",
    georgia: "Georgia, serif",
    times: "Times New Roman, Times, serif",
    courier: "Courier New, Courier, monospace",
  })[family ?? "arial"];

const safeHttpUrl = (value: unknown, allowEmpty = false): value is string => {
  if (allowEmpty && value === "") return true;
  if (typeof value !== "string" || value.length > 2_100_000) return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const logoDesignToSvgDataUrl = (design: LogoDesign) => {
  const width = 700;
  const height = 200;
  const elements = design.elements
    .map((element) => {
      const x = (element.x / 100) * width;
      const y = (element.y / 100) * height;
      const elementWidth = (element.width / 100) * width;
      const elementHeight = (element.height / 100) * height;
      if (element.type === "image") {
        return `<image x="${x}" y="${y}" width="${elementWidth}" height="${elementHeight}" href="${escapeXml(element.src)}" preserveAspectRatio="xMidYMid meet" />`;
      }
      const textX =
        element.align === "left"
          ? x
          : element.align === "right"
            ? x + elementWidth
            : x + elementWidth / 2;
      const anchor =
        element.align === "left"
          ? "start"
          : element.align === "right"
            ? "end"
            : "middle";
      return `<text x="${textX}" y="${y + elementHeight / 2}" fill="${escapeXml(element.color)}" font-family="${escapeXml(logoFontFamily(element.fontFamily))}" font-size="${element.fontSize}" font-weight="${element.fontWeight}" text-anchor="${anchor}" dominant-baseline="middle">${escapeXml(element.text)}</text>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="${width}" height="${height}" rx="${design.cornerRadius}" fill="${escapeXml(design.backgroundColor)}" />${elements}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const validLogo = (value: unknown): value is LogoDesign => {
  if (!isObject(value)) return false;
  if (
    !isHexColor(value.backgroundColor) ||
    !boundedNumber(value.cornerRadius, 0, 50) ||
    !Array.isArray(value.elements) ||
    value.elements.length > 20
  ) {
    return false;
  }
  return value.elements.every((entry) => {
    if (!isObject(entry)) return false;
    const base =
      typeof entry.id === "string" &&
      entry.id.length <= 80 &&
      boundedNumber(entry.x, 0, 100) &&
      boundedNumber(entry.y, 0, 100) &&
      boundedNumber(entry.width, 1, 100) &&
      boundedNumber(entry.height, 1, 100);
    if (!base) return false;
    if (entry.type === "text") {
      return (
        typeof entry.text === "string" &&
        entry.text.length <= 160 &&
        isHexColor(entry.color) &&
        (entry.fontFamily === undefined ||
          [
            "arial",
            "verdana",
            "trebuchet",
            "georgia",
            "times",
            "courier",
          ].includes(entry.fontFamily as string)) &&
        boundedNumber(entry.fontSize, 8, 160) &&
        [400, 600, 700, 800].includes(entry.fontWeight as number) &&
        ["left", "center", "right"].includes(entry.align as string)
      );
    }
    return (
      entry.type === "image" &&
      typeof entry.name === "string" &&
      entry.name.length <= 160 &&
      typeof entry.src === "string" &&
      entry.src.length <= 2_100_000 &&
      (entry.src.startsWith("data:image/") || safeHttpUrl(entry.src))
    );
  });
};

const validLinks = (value: unknown): value is CustomerLink[] =>
  Array.isArray(value) &&
  value.length <= 3 &&
  value.every(
    (entry) =>
      isObject(entry) &&
      ["website", "privacy", "imprint"].includes(entry.id as string) &&
      typeof entry.label === "string" &&
      entry.label.length <= 40 &&
      safeHttpUrl(entry.url, true),
  );

const validActions = (value: unknown): value is CustomerLogoAction[] =>
  Array.isArray(value) &&
  value.length <= 6 &&
  value.every(
    (entry) =>
      isObject(entry) &&
      typeof entry.id === "string" &&
      entry.id.length <= 80 &&
      ["website", "whatsapp", "email", "phone", "link"].includes(
        entry.type as string,
      ) &&
      typeof entry.label === "string" &&
      entry.label.length <= 40 &&
      typeof entry.value === "string" &&
      entry.value.length <= 500,
  );

const validRuntimeEnvelope = (
  value: unknown,
): value is RuntimeTenantEnvelope => {
  if (!isObject(value) || !isObject(value.configuration)) return false;
  const config = value.configuration;
  return (
    typeof value.id === "string" &&
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u.test(value.id) &&
    typeof value.domain === "string" &&
    value.domain.length <= 253 &&
    config.schemaVersion === 1 &&
    typeof config.identityName === "string" &&
    config.identityName.length >= 1 &&
    config.identityName.length <= 120 &&
    isHexColor(config.primaryColor) &&
    ["light", "dark"].includes(config.defaultTheme as string) &&
    ["de", "en", "both"].includes(config.languageMode as string) &&
    ["sailplot", "primary", "yellow", "red", "custom"].includes(
      config.markColorMode as string,
    ) &&
    isHexColor(config.markCustomColor) &&
    isHexColor(config.startLineFlagColor) &&
    validLinks(config.links) &&
    validActions(config.logoActions) &&
    validLogo(config.logo)
  );
};

const resolveLogoActions = (actions: CustomerLogoAction[]) =>
  actions.flatMap((action) => {
    const label = action.label.trim();
    const value = action.value.trim();
    if (!label || !value) return [];
    if (action.type === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value)
        ? [{ label, url: `mailto:${value}` }]
        : [];
    }
    if (action.type === "phone" || action.type === "whatsapp") {
      if (!/^\+?[0-9 ()-]{6,30}$/u.test(value)) return [];
      const phone = value.replace(/[^0-9+]/gu, "");
      return [
        {
          label,
          url:
            action.type === "whatsapp"
              ? `https://wa.me/${phone.replace(/\D/gu, "")}`
              : `tel:${phone}`,
        },
      ];
    }
    return safeHttpUrl(value)
      ? [{ label, url: new URL(value).toString() }]
      : [];
  });

const linkFor = (record: CustomerTenantRecord, id: CustomerLink["id"]) =>
  record.links.find((link) => link.id === id)?.url || null;

const sailPlotLegalBaseUrlFor = (domain: string) =>
  domain.endsWith("-sandbox.sailplot.app") ||
  domain.endsWith(".sandbox.sailplot.app")
    ? "https://sandbox.sailplot.app/"
    : "https://sailplot.app/";

const partnerLinks = (record: CustomerTenantRecord) => {
  const source = record.logoLinks ?? record.links;
  const links = source
    .filter((link) => link.url)
    .map(({ label, url }) => ({ label, url }));
  return record.logoLinks ? links : links.length ? links : null;
};

export const createCustomerTenant = (
  record: CustomerTenantRecord,
): PlatformTenant => {
  const customerPrivacyUrl = linkFor(record, "privacy");
  const customerImprintUrl = linkFor(record, "imprint");

  return {
    id: record.id,
    domains: [record.domain],
    config: {
      branding: {
        appName: record.identityName,
        shortName: record.identityName,
        logo: coreAssets.sailplotLogoLight,
        logoDark: coreAssets.sailplotLogoDark,
        compactLogo: coreAssets.sailplotIcon,
        logoAlt: "SailPlot",
        favicon: sailPlotFaviconDataUrl(record.primaryColor),
        partnerName: record.identityName,
        partnerLabel: "",
        partnerLogo: record.logoDataUrl,
        partnerLinks: partnerLinks(record),
        exportWatermarkLogo: record.logoDataUrl,
        exportProductLogo: coreAssets.sailplotLogoLight,
      },
      theme: {
        mode: record.defaultTheme,
        usePrimaryForBrandAccents: true,
        qrFinderColor: null,
        light: { primary: record.primaryColor, primaryText: "#FFFFFF" },
        dark: { primary: record.primaryColor, primaryText: "#FFFFFF" },
      },
      links: {
        app: `https://${record.domain}/`,
        website: linkFor(record, "website"),
        privacy: customerPrivacyUrl,
        imprint: customerImprintUrl,
      },
      ui: {
        help: false,
        canvasBrandingLinks: false,
        poweredBySailPlot: false,
      },
      objectColors: {
        markColorMode: record.markColorMode,
        markCustomColor:
          record.markColorMode === "custom" ? record.markCustomColor : null,
        startLineFlagColor: record.startLineFlagColor,
      },
      localization: { languageMode: record.languageMode },
      pageTitle: sailPlotCustomizedPageTitle(record.identityName),
      storageNamespace: `sailplot-platform-${record.id}`,
    },
    extensions: createPlatformPageExtensions({
      customerLegalLinks: {
        imprint: customerImprintUrl ?? undefined,
        privacy: customerPrivacyUrl ?? undefined,
      },
      hostingProvider: "cloudflare-pages",
      imprint: false,
      instructions: true,
      makeYourOwn: true,
      openSource: true,
      privacy: false,
      sailPlotLegalBaseUrl: sailPlotLegalBaseUrlFor(record.domain),
      hostedForCustomer: true,
    }),
  };
};

export const runtimeCustomerTenant = (
  sourceDocument?: Document,
): PlatformTenant | null => {
  const runtimeDocument =
    sourceDocument ?? (typeof document === "undefined" ? undefined : document);
  const element = runtimeDocument?.getElementById("sailplot-runtime-tenant");
  if (!element?.textContent) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(element.textContent) as unknown;
  } catch {
    return null;
  }
  if (!validRuntimeEnvelope(payload)) return null;
  const config = payload.configuration;
  return createCustomerTenant({
    id: payload.id,
    domain: payload.domain,
    identityName: config.identityName,
    primaryColor: config.primaryColor,
    defaultTheme: config.defaultTheme,
    languageMode: config.languageMode,
    markColorMode: config.markColorMode,
    markCustomColor: config.markCustomColor,
    startLineFlagColor: config.startLineFlagColor,
    logoDataUrl: logoDesignToSvgDataUrl(config.logo),
    links: config.links,
    logoLinks: resolveLogoActions(config.logoActions),
  });
};
